import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../hooks/useApi";
import { ApiError, api } from "../lib/api";
import { MINDCODE_HOST_ID, findMindcodeNeedingToken } from "../lib/mindcode-card";
import { AdminAlert } from "./AdminAlert";
import { InfoTooltip } from "./InfoTooltip";

const DISMISS_KEY = "msq.agent-hub.mindcode-card-dismissed";

/**
 * Post-Login First-Run-Karte (#117-Follow-up): erscheint für Admins, solange der
 * vorbelegte mindcode-Git-Host noch kein Token hat, und fragt Forgejo-Token +
 * Benutzername nach. Ein Forgejo-Token deckt Forge **und** Registry ab — daher
 * wird der Token in beide Token-Felder geschrieben und der Benutzername als
 * Registry-User gesetzt (für `docker login`/private Image-Pulls). Funktioniert in
 * beiden Auth-Modi (Showroom + Entra), da rein post-login. Überspringbar.
 */
export function MindcodeTokenCard(): JSX.Element | null {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const hosts = useApi(() => api.admin.gitHosts.list(), [refresh]);
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed || hosts.status !== "ready") return null;
  const host = findMindcodeNeedingToken(hosts.data.hosts);
  if (!host) return null;

  const dismiss = (): void => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // localStorage kann blockiert sein — dann nur für diese Session ausblenden.
    }
    setDismissed(true);
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Ein Forgejo-Token deckt Forge + Registry ab → in beide Felder sealen.
      await api.admin.gitHosts.update(MINDCODE_HOST_ID, {
        forge_token: token,
        registry_username: username.trim(),
        registry_password: token,
      });
      setRefresh((r) => r + 1); // has_forge_token=true → Karte verschwindet
    } catch (err) {
      setError(err instanceof ApiError ? (err.detail ?? err.message) : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="admin__form" aria-labelledby="mindcode-card-title">
      <h3 id="mindcode-card-title">{t("admin.mindcode.title")}</h3>
      <p className="launchpad__hint">{t("admin.mindcode.body")}</p>
      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}
      <form onSubmit={(e) => void submit(e)}>
        <p className="form__req-note">{t("common.requiredHint")}</p>
        <div className="admin__field">
          <label htmlFor="mindcode-username">
            {t("admin.mindcode.username")}
            <span className="field-req" aria-hidden="true">
              *
            </span>
          </label>
          <input
            id="mindcode-username"
            type="text"
            autoComplete="username"
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div className="admin__field">
          <span className="field-head">
            <label htmlFor="mindcode-token">
              {t("admin.mindcode.token")}
              <span className="field-req" aria-hidden="true">
                *
              </span>
            </label>
            <InfoTooltip text={t("admin.mindcode.tokenHint")} />
          </span>
          <input
            id="mindcode-token"
            type="password"
            autoComplete="off"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>
        {busy ? (
          <output className="admin__busy" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            {t("common.saving")}
          </output>
        ) : null}
        <div className="admin__form-actions">
          <button type="submit" className="primary" disabled={busy}>
            {t("admin.mindcode.save")}
          </button>
          <button type="button" onClick={dismiss} disabled={busy}>
            {t("admin.mindcode.later")}
          </button>
        </div>
      </form>
    </section>
  );
}
